import React, { useId } from 'react';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { IconRubricUnion16Svg } from 'apps/sigma2-ui/src/components/svg/IconRubricUnion16';
import { S2Typography } from 'apps/sigma2-ui/src/components/ui/Typography';
import cn from 'classnames';
import { useLanguage } from 'apps/sigma2-ui/src/lib/hooks/useLanguage';
import { TouchTarget } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { TouchTargetBehavior } from 'apps/sigma2-ui/src/components/ui/TouchTarget';
import { RubricWhiteCard } from 'apps/sigma2-ui/src/components/rubrics/RubricWhiteCard';

export interface RubricListHeaderCardsProps {
  resumeRubricHref: string;
  writtenResponseRubricHref: string;
  interviewRubricHref: string;
}

export const RubricListHeaderCards: React.FC<RubricListHeaderCardsProps> = (
  props,
) => {
  const language = useLanguage();

  const whiteCardClassName = 'flex-auto md:flex-1 w-100% md:w-auto';

  return (
    <Spacing direction="horizontal" size="md" className={'w-full flex-wrap'}>
      <RubricListHeaderWhiteCard
        icon={'/images/rubrics/rubric-resume-large.png'}
        titleTranslatedContent={language.resumeRubricTitle()}
        subtitleTranslatedContent={language.resumeRubricDescription()}
        behavior={{ type: 'href', href: props.resumeRubricHref }}
        widthClassName={whiteCardClassName}
      />
      <RubricListHeaderWhiteCard
        icon={'/images/rubrics/rubric-questions-large.png'}
        titleTranslatedContent={language.writtenResponseRubricTitle()}
        subtitleTranslatedContent={language.writtenResponseRubricDescription()}
        behavior={{ type: 'href', href: props.writtenResponseRubricHref }}
        widthClassName={whiteCardClassName}
      />
      <RubricListHeaderWhiteCard
        icon={'/images/rubrics/rubric-interview-large.png'}
        titleTranslatedContent={language.interviewRubricTitle()}
        subtitleTranslatedContent={language.interviewRubricDescription()}
        behavior={{ type: 'href', href: props.interviewRubricHref }}
        widthClassName={whiteCardClassName}
      />
      <RubricPurpleCard className={'w-[100%] md:w-[20%]'} />
    </Spacing>
  );
};

export interface RubricPurpleCardProps {
  className?: string;
}

export const RubricPurpleCard: React.FC<RubricPurpleCardProps> = (props) => {
  const language = useLanguage();

  return (
    <div
      className={cn(
        'relative overflow-hidden rounded-lg p-4 shadow-2px',
        props.className,
      )}
    >
      <RubricPurpleCardBackground />
      <img
        src="/images/rubrics/rubric-purple-card-background-icon.png"
        className={'absolute right-[-38px] bottom-[-38px] z-10 select-none'}
      />
      <div className={'relative z-20'}>
        <Spacing size={'lg'} direction={'vertical'}>
          <Spacing
            size={'md'}
            direction={'horizontal'}
            className={'items-center'}
          >
            <IconRubricUnion16Svg
              svg={{ className: 'h-[17px] w-[16px] text-white' }}
            />
            <S2Typography
              variant={'headline-6-alt'}
              textColorVariant={'white'}
              translatedContent={language.rubricPurpleCardTitle()}
            />
          </Spacing>
          <S2Typography
            variant={'body-6'}
            textColorVariant={'white'}
            translatedContent={language.rubricPurpleCardDescription()}
          />
        </Spacing>
      </div>
    </div>
  );
};

type RubricPurpleCardBackgroundProps = React.SVGProps<SVGSVGElement>;

export const RubricPurpleCardBackground: React.FC<
  RubricPurpleCardBackgroundProps
> = ({ style, ...props }: RubricPurpleCardBackgroundProps) => {
  const id = useId();

  const maskId = `mask-${id}`;
  const blur1Id = `blur1-${id}`;
  const blur2Id = `blur2-${id}`;
  const bgGradientId = `bgGradient-${id}`;
  const orbGradientId = `orbGradient-${id}`;

  return (
    <svg
      viewBox="0 0 211 188"
      width="100%"
      height="100%"
      preserveAspectRatio="none"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
      style={{
        position: 'absolute',
        inset: 0,
        display: 'block',
        pointerEvents: 'none',
        ...style,
      }}
      {...props}
    >
      {/* Base card fill */}
      <rect x="0" y="0" width="211" height="188" fill="white" />
      <rect
        x="0"
        y="0"
        width="211"
        height="188"
        fill={`url(#${bgGradientId})`}
      />

      {/* Clip the blobs to the card shape */}
      <mask
        id={maskId}
        maskUnits="userSpaceOnUse"
        x="0"
        y="0"
        width="211"
        height="188"
        style={{ maskType: 'alpha' }}
      >
        <rect x="0" y="0" width="211" height="188" fill="#080857" />
      </mask>

      <g mask={`url(#${maskId})`}>
        <g filter={`url(#${blur1Id})`}>
          <ellipse
            cx="168.216"
            cy="186.145"
            rx="168.216"
            ry="186.145"
            transform="matrix(0.563664 -0.826004 0.753586 0.65735 -145.836 46.085)"
            fill="#3A3AE8"
          />
        </g>

        <g filter={`url(#${blur2Id})`}>
          <ellipse
            cx="92.3723"
            cy="96.512"
            rx="92.3723"
            ry="96.512"
            transform="matrix(0.563664 -0.826004 0.753586 0.65735 -87.9141 -24.1981)"
            fill={`url(#${orbGradientId})`}
          />
        </g>
      </g>

      <defs>
        <filter
          id={blur1Id}
          x="-180.078"
          y="-255.647"
          width="538.672"
          height="570.296"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="50" result="effect1_foregroundBlur" />
        </filter>

        <filter
          id={blur2Id}
          x="-152.57"
          y="-236.288"
          width="378.906"
          height="398.465"
          filterUnits="userSpaceOnUse"
          colorInterpolationFilters="sRGB"
        >
          <feFlood floodOpacity="0" result="BackgroundImageFix" />
          <feBlend in="SourceGraphic" in2="BackgroundImageFix" result="shape" />
          <feGaussianBlur stdDeviation="50" result="effect1_foregroundBlur" />
        </filter>

        <radialGradient
          id={bgGradientId}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(54.5 -60.233 67.6019 50.9451 19 176.136)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#E5E5FF" />
          <stop offset="1" stopColor="white" />
        </radialGradient>

        <radialGradient
          id={orbGradientId}
          cx="0"
          cy="0"
          r="1"
          gradientTransform="matrix(1.27719 106.395 -91.9846 0.949409 92.3723 96.512)"
          gradientUnits="userSpaceOnUse"
        >
          <stop stopColor="#080857" />
          <stop offset="0.580737" stopColor="#552BD3" />
          <stop offset="0.947115" stopColor="#BDACF6" />
        </radialGradient>
      </defs>
    </svg>
  );
};

export interface RubricListHeaderWhiteCardProps {
  className?: string;
  icon: string;
  titleTranslatedContent: string;
  subtitleTranslatedContent: string;
  behavior: TouchTargetBehavior;
  widthClassName: string;
}

export const RubricListHeaderWhiteCard: React.FC<
  RubricListHeaderWhiteCardProps
> = (props) => {
  return (
    <RubricWhiteCard
      className={cn(
        props.widthClassName,
        'shadow-0px hover:shadow-soft-xl hover:-translate-y-1',
      )}
      roundedClassName={'rounded-xl'}
    >
      <TouchTarget
        className="cursor-default"
        content={() => {
          return (
            <Spacing
              size={'md'}
              direction={'vertical'}
              className={'cursor-pointer'}
            >
              <img src={props.icon} className={'w-[64px] h-[64px]'} alt="" />
              <Spacing size={'none'} direction={'vertical'}>
                <S2Typography
                  variant={'headline-6-alt'}
                  translatedContent={props.titleTranslatedContent}
                  textColorVariant={'indigo-900'}
                />
                <S2Typography
                  variant={'body-6'}
                  translatedContent={props.subtitleTranslatedContent}
                />
              </Spacing>
            </Spacing>
          );
        }}
        behavior={props.behavior}
      />
    </RubricWhiteCard>
  );
};
